"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";

/*
 * Le moteur lottie-react pèse 314 653 octets bruts. Importé en statique, il
 * entrait dans le bundle partagé de toutes les pages, y compris celles qui
 * n'affichent aucune animation. Chargé à la demande, il n'arrive que quand une
 * animation est réellement montée, et jamais sur le chemin critique du premier
 * rendu.
 */
const Lottie = dynamic(() => import("@/components/ui/LottiePlayer/LottiePlayer"), { ssr: false });

/*
 * Les JSON d'animation sont lourds : auth.json fait 2 296 333 octets, trip.json
 * 526 177. Importés en statique, ils ne sont pas des fichiers chargés à la
 * demande : le bundler les inline dans le morceau de JavaScript de la page, qui
 * est donc téléchargé ET analysé avant que la page soit interactive, même si
 * l'animation est sous la ligne de flottaison ou jamais montée.
 *
 * `animationSrc` les charge par le réseau, depuis /static/animations/. Le cache
 * ci-dessous évite de retélécharger le même fichier quand plusieurs composants
 * de la même page affichent la même animation : bebot.json est utilisé par
 * douze composants.
 *
 * La prop `animation` continue de fonctionner : les deux coexistent, et on
 * migre les appels un par un.
 */
const CACHE = new Map();

function useAnimationDistante(src) {
    const [donnees, setDonnees] = useState(() => (src ? CACHE.get(src) : null));

    useEffect(() => {
        if (!src || CACHE.has(src)) return;
        let vivant = true;
        fetch(src)
            .then((r) => (r.ok ? r.json() : null))
            .then((json) => {
                if (!json) return;
                CACHE.set(src, json);
                // Le composant a pu être démonté pendant le téléchargement.
                if (vivant) setDonnees(json);
            })
            .catch(() => {
                // Une animation absente ne doit rien casser : on ne dessine
                // simplement rien à sa place.
            });
        return () => {
            vivant = false;
        };
    }, [src]);

    return src ? donnees : null;
}

// Callback par défaut défini une fois pour toutes, plutôt qu'un () => {}
// recréé à chaque render.
const NOOP = () => {};

// lottie-react 3 nomme le sens de lecture ; on garde 1 / -1 en interne.
const sens = (dir) => (dir === 1 ? "forward" : "reverse");

// Dernière frame, lue dans le JSON (op - ip frames, comme lottie-web). Au
// moment où l'animation est prête, la poignée de lottie-react n'a pas toujours
// reçu son animationItem, qui lui arrive par un état React.
function derniereFrame(json) {
    const total = json ? Math.floor(json.op - json.ip) : NaN;
    return Number.isFinite(total) ? Math.max(0, total - 1) : 0;
}

// mode : "loop" (défaut, rétrocompat = boucle infinie) | "once" (une seule
// lecture puis arrêt) | "bounce" (aller-retour : le sens s'inverse à chaque
// fin de boucle) | "loop-reverse" / "once-reverse" (lecture de la FIN vers le
// début, en boucle ou une seule fois). speed : multiplicateur (1 = normal).
export default function PublicLottieAnimation({
    animation,
    animationSrc,
    width = "100%",
    stopAutoPlay = false,
    className = "",
    ref = null,
    onDomLoaded = NOOP,
    speed = 1,
    mode = "loop",
}) {
    const innerRef = useRef(null);
    const dirRef = useRef(1);

    // `animation` (import statique) reste prioritaire pour ne rien changer aux
    // appels existants ; `animationSrc` prend le relais quand il est fourni.
    const distante = useAnimationDistante(animationSrc);
    const donnees = animation ?? distante;

    // On ne pilote l'instance (vitesse, sens) que si un réglage non-défaut le
    // demande. Sinon aucun lottieRef n'est transmis : l'animation se contente
    // de jouer, comme historiquement pour le picker et les autres usages.
    const needsInstance = speed !== 1 || mode !== "loop";
    const controlRef = ref || (needsInstance ? innerRef : null);

    // Valeurs derrière des refs : les handlers lisent toujours la valeur à
    // jour, sans dépendre d'elles.
    const onDomLoadedRef = useRef(onDomLoaded);
    onDomLoadedRef.current = onDomLoaded;
    const speedRef = useRef(speed);
    speedRef.current = speed;
    const modeRef = useRef(mode);
    modeRef.current = mode;

    const handleDomLoaded = useCallback(() => {
        // On n'applique vitesse/sens QUE quand on pilote l'animation : un
        // appelant qui a son propre ref garde le contrôle exclusif.
        if (needsInstance) {
            const inst = controlRef && controlRef.current;
            if (inst) {
                if (typeof inst.setSpeed === "function") {
                    inst.setSpeed(speedRef.current > 0 ? speedRef.current : 1);
                }
                const reversed =
                    modeRef.current === "loop-reverse" ||
                    modeRef.current === "once-reverse";
                dirRef.current = reversed ? -1 : 1;
                if (typeof inst.setDirection === "function") {
                    inst.setDirection(sens(dirRef.current));
                }
                // Lecture inversée : on se cale sur la DERNIÈRE frame puis on
                // joue vers le début (l'autoplay est coupé pour ces modes).
                if (reversed && typeof inst.seek === "function") {
                    inst.seek(derniereFrame(donnees));
                    inst.play();
                }
            }
        }
        onDomLoadedRef.current();
    }, [controlRef, needsInstance, donnees]);

    // Aller-retour : on N'UTILISE PAS le loop natif de lottie-web (qui, à la fin
    // d'une itération, saute à la 1re frame AVANT qu'on inverse le sens → flash
    // apparaît/disparaît). À la place, loop désactivé + écoute de "complete"
    // (fin réelle atteinte dans le sens courant) : on inverse le sens et on
    // relance depuis la position ACTUELLE (aucun saut, aucun flash).
    const handleComplete = useCallback(() => {
        const inst = controlRef && controlRef.current;
        if (!inst) return;
        if (modeRef.current === "bounce") {
            if (
                typeof inst.setDirection === "function" &&
                typeof inst.play === "function"
            ) {
                dirRef.current = dirRef.current === 1 ? -1 : 1;
                inst.setDirection(sens(dirRef.current));
                inst.play();
            }
            return;
        }
        // Boucle inversée : le loop natif de lottie-web ne rejoue pas à
        // l'envers proprement → on relance chaque itération depuis la fin.
        if (
            modeRef.current === "loop-reverse" &&
            typeof inst.seek === "function"
        ) {
            inst.seek(derniereFrame(donnees));
            inst.play();
        }
    }, [controlRef, donnees]);

    // Rien à dessiner tant que le JSON n'est pas arrivé. On réserve la largeur
    // pour que l'arrivée de l'animation ne décale pas la mise en page.
    if (!donnees) {
        return <div style={{ width: width }} className={className} aria-hidden />;
    }

    return (
        <Lottie
            src={donnees}
            // Les modes inversés démarrent à la main (depuis la dernière
            // frame) dans handleDomLoaded — l'autoplay partirait du début.
            autoplay={
                !stopAutoPlay &&
                mode !== "loop-reverse" &&
                mode !== "once-reverse"
            }
            // Boucle native seulement en mode "loop". "once" et "bounce" ne
            // bouclent pas nativement : "once" s'arrête, "bounce" est relancé
            // à l'envers par handleComplete.
            loop={mode === "loop"}
            style={{ width: width }}
            className={className}
            lottieRef={controlRef}
            subscriptions={{
                ready: handleDomLoaded,
                complete: handleComplete,
            }}
        />
    );
}
